import React, { useState, useEffect } from 'react';
import { businessAPI } from '../../api/businesses';
import AddServiceModal from './AddServiceModal';
import { PencilIcon, TrashIcon, PlusIcon, ClockIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { resolveImageUrl } from '../../utils/formatters';

const ServiceList = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [businessId, setBusinessId] = useState(null);
  const [businessType, setBusinessType] = useState(null);
  const [businessTimezone, setBusinessTimezone] = useState('UTC');
  const [businessCurrencySymbol, setBusinessCurrencySymbol] = useState('$');
  const [workingHours, setWorkingHours] = useState(null);
  const [businessHoursLocalText, setBusinessHoursLocalText] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const defaultWorkingHours = {
    monday: [{ start: '09:00', end: '22:00' }],
    tuesday: [{ start: '09:00', end: '22:00' }],
    wednesday: [{ start: '09:00', end: '22:00' }],
    thursday: [{ start: '09:00', end: '22:00' }],
    friday: [{ start: '09:00', end: '22:00' }],
    saturday: [{ start: '09:00', end: '22:00' }],
    sunday: []
  };

  const formatHmToAmPm = (timeStr) => {
    if (!timeStr) return '';
    const [hhRaw, mmRaw] = String(timeStr).split(':');
    const hh = Number(hhRaw);
    const mm = Number(mmRaw ?? 0);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return timeStr;
    const ampm = hh >= 12 ? 'PM' : 'AM';
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
  };

  useEffect(() => {
    try {
      const source = workingHours && typeof workingHours === 'object'
        ? workingHours
        : defaultWorkingHours;

      const daysOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const firstDay = daysOrder.find((d) => Array.isArray(source[d]) && source[d].length > 0) || null;
      const periods = firstDay ? source[firstDay] : [];
      if (!periods || periods.length === 0) {
        setBusinessHoursLocalText('Closed');
        return;
      }

      const start = periods[0]?.start;
      const end = periods[periods.length - 1]?.end;
      const startText = formatHmToAmPm(start);
      const endText = formatHmToAmPm(end);
      setBusinessHoursLocalText(startText && endText ? `${startText} - ${endText}` : (startText || endText || 'Not set'));
    } catch (e) {
      setBusinessHoursLocalText('');
    }
  }, [businessTimezone, workingHours]);

  const loadData = async () => {
    try {
      setLoading(true);
      // First get the business ID of the current user
      const bizRes = await businessAPI.getMyBusiness();
      const biz = bizRes.data.data;
      const bizId = biz.id;
      setBusinessId(bizId);
      setBusinessType(biz.business_type);
      setBusinessTimezone(biz.timezone || 'UTC');
      setBusinessCurrencySymbol(biz.currency_symbol || '$');
      setWorkingHours(biz.settings?.working_hours || null);
      
      // Then load services for that business
      const response = await businessAPI.getServices(bizId);
      setServices(response.data.data);
    } catch (error) {
      console.error('Failed to load services:', error);
    } finally {
      setLoading(false);
    }
  };

  const daysOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabel = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun'
  };

  const getAvailableDaysText = (serviceAvailability) => {
    const availabilitySource = serviceAvailability && typeof serviceAvailability === 'object'
      ? serviceAvailability
      : (workingHours && typeof workingHours === 'object' ? workingHours : null);

    if (!availabilitySource) return 'Not set';

    const openDays = daysOrder.filter((d) => Array.isArray(availabilitySource[d]) && availabilitySource[d].length > 0);
    if (openDays.length === 0) return 'Closed';
    return openDays.map((d) => dayLabel[d] || d).join(', ');
  };

  const loadServices = async (bizId) => {
    try {
      const response = await businessAPI.getServices(bizId || businessId);
      setServices(response.data.data);
    } catch (error) {
      console.error('Failed to load services:', error);
    }
  };

  const handleDelete = async (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await businessAPI.deleteService(businessId, serviceId);
        await loadServices(businessId);
        alert('Service deleted successfully');
      } catch (error) {
        console.error('Failed to delete service:', error);
      }
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Services</h1>
          <p className="text-gray-600">Manage your service offerings</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Service</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service) => (
          <div key={service.id} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-start">
              <div className="flex items-start gap-3 min-w-0">
                {service.image && !imageErrors[service.id] ? (
                  <img
                    src={resolveImageUrl(service.image)}
                    alt={service.name}
                    className="h-12 w-12 rounded-xl object-cover border bg-white"
                    onError={() => setImageErrors((prev) => ({ ...prev, [service.id]: true }))}
                  />
                ) : (
                  <div className="h-12 w-12 rounded-xl border bg-gray-50 flex items-center justify-center text-xl">
                    ✂️
                  </div>
                )}
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{service.name}</h3>
                  <p className="text-2xl font-bold text-primary-600 mt-2">{businessCurrencySymbol}{service.price}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setEditingService(service)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleDelete(service.id)}
                  className="text-red-400 hover:text-red-600"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <p className="text-gray-600 text-sm mt-2">{service.description}</p>
            
            <div className="mt-4 pt-4 border-t">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Duration:</span>
                <span className="font-medium">{service.duration_minutes} minutes</span>
              </div>
              {/* <div className="flex justify-between text-sm mt-1">
                <span className="text-gray-500">Buffer Time:</span>
                <span className="font-medium">
                  {service.buffer_before} min before / {service.buffer_after} min after
                </span>
              </div> */}
              <div className="flex justify-between text-sm mt-3">
                <span className="text-gray-500 flex items-center gap-1.5">
                  <ClockIcon className="h-4 w-4 text-gray-400" />
                  Business hours:
                </span>
                <span className="font-medium">{businessHoursLocalText || 'Not set'}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-gray-500 flex items-center gap-1.5">
                  <CalendarDaysIcon className="h-4 w-4 text-gray-400" />
                  Available days:
                </span>
                <span className="font-medium text-right">{getAvailableDaysText(service.availability)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {(showAddModal || editingService) && (
        <AddServiceModal
          service={editingService}
          businessId={businessId}
          businessType={businessType}
          currencySymbol={businessCurrencySymbol}
          onClose={() => {
            setShowAddModal(false);
            setEditingService(null);
          }}
          onSuccess={() => {
            setShowAddModal(false);
            setEditingService(null);
            loadServices(businessId);
          }}
        />
      )}
    </div>
  );
};

export default ServiceList;
